import { DEFAULT_LANDING, NO_ACCESS_PATH, resolveLanding } from '../utils/nav-sections.utils';
import { can, isPermissionDeclared, isSuperAdmin, loadPermissions } from '../utils/permissions';

// Routes every logged-in admin may open, whatever their tags.
const UNGUARDED_PATHS = ['/login', '/profile', NO_ACCESS_PATH];

export function isUnguarded(path) {
  return UNGUARDED_PATHS.includes(path) || path.startsWith('/mfa/');
}

/**
 * Whether the logged-in admin may open a route (specs 131/132). Every child
 * route declares `meta.permission` (contracts/frontend-action-map.md), a tag
 * or a list of alternative tags (any-of); `tests/unit/router/route-permissions.spec.js`
 * fails on a route without one. If one slips through anyway it is Super admin
 * only, the same rule as the menu (`isSectionAllowed`).
 * @param {{ meta?: { permission?: string|string[] } }} route
 * @returns {boolean}
 */
export function isRouteAllowed(route) {
  const permission = route.meta && route.meta.permission;
  return isPermissionDeclared(permission) ? can(permission) : isSuperAdmin();
}

/**
 * Global `beforeEach`.
 *
 * - `/` (also where login lands) resolves the admin's landing (spec 028) with a
 *   fresh `/me`, filtered by their tags. It cannot be a route `redirect`:
 *   redirects are synchronous and resolved before any guard.
 * - Any other route under Home is checked against the cached tags and sent to
 *   `/sin-acceso` when not allowed. Deep links that are allowed are never redirected.
 *
 * On a failed `/me` the navigation continues and Home.vue's own check sends the
 * admin to `/login` or `/mfa/setup`, exactly as before.
 */
export async function permissionGuard(to, from, next) {
  if (isUnguarded(to.path)) return next();

  if (to.path === '/') {
    const res = await loadPermissions({ refresh: true });
    if (res.status !== 'ok') return next({ path: DEFAULT_LANDING, replace: true });
    const preferences = res.data.preferences;
    return next({ path: resolveLanding(preferences && preferences.defaultSection), replace: true });
  }

  const res = await loadPermissions();
  if (res.status !== 'ok' || isRouteAllowed(to)) return next();
  return next({ path: NO_ACCESS_PATH, replace: true });
}
